/* eslint-disable max-len */
import {
  DEVELOPER_EXECUTIVE,
  DEVELOPER_HEADQUARTERS,
  DEVELOPER_BRANCH_LEADER_SUPERVISOR,
  DEVELOPER_BRANCH_LEADER_SUPERVISOR_II,
  DEVELOPER_BRANCH_LEADER,
  DEVELOPER_AGENCY_ADMIN,
  DEVELOPER_AGENT_LEADER,
} from './groups';

// Level 1
export const ROLE_HEADQUARTERS = 'Headquarters';
// Level 2
export const ROLE_SUPERVISOR = 'Supervisor';
export const ROLE_SALES_MANAGER = 'Sales Manager';
export const ROLE_SALES_ADMIN = 'Sales Admin';
// Level 2.5
export const ROLE_AGENCY_LEADER = 'Agency Leader';
export const ROLE_AGENCY_ADMIN = 'Agency Admin';
// Level 3
export const ROLE_STAFF = 'Staff';
export const ROLE_AGENT = 'Agent';

// From backend, 1 = Headquarters ...
export const ROLE_MAP = [
  undefined,
  ROLE_HEADQUARTERS,
  ROLE_SUPERVISOR,
  ROLE_SALES_MANAGER,
  ROLE_SALES_ADMIN,
  ROLE_STAFF,
];

export const ROLE_HEADQUARTERS_GROUPS = [
  DEVELOPER_EXECUTIVE,
  DEVELOPER_HEADQUARTERS,
];

export const ROLE_SUPERVISOR_GROUPS = [
  DEVELOPER_EXECUTIVE,
  DEVELOPER_BRANCH_LEADER_SUPERVISOR,
];

export const ROLE_SALES_MANAGER_GROUPS = [
  DEVELOPER_EXECUTIVE,
  DEVELOPER_BRANCH_LEADER_SUPERVISOR_II,
];

export const ROLE_SALES_ADMIN_GROUPS = [
  DEVELOPER_EXECUTIVE,
  DEVELOPER_BRANCH_LEADER,
];

export const ROLE_AGENCY_ADMIN_GROUPS = [
  DEVELOPER_EXECUTIVE,
  DEVELOPER_AGENCY_ADMIN,
];

export const ROLE_AGENCY_LEADERS_GROUPS = [
  DEVELOPER_EXECUTIVE,
  DEVELOPER_AGENT_LEADER,
];

export const STAFF_GROUPS = [DEVELOPER_EXECUTIVE];

export function isHeadquartersRole(groups = []) {
  return Array.isArray(groups) && groups.length > 0 && ROLE_HEADQUARTERS_GROUPS.every((hg) => groups.includes(hg));
}

export function isSupervisorRole(groups = []) {
  return Array.isArray(groups) && groups.length > 0 && ROLE_SUPERVISOR_GROUPS.every((sg) => groups.includes(sg));
}

export function isSalesManagerRole(groups = []) {
  return Array.isArray(groups) && groups.length > 0 && ROLE_SALES_MANAGER_GROUPS.every((smg) => groups.includes(smg));
}

export function isSalesAdminRole(groups = []) {
  return Array.isArray(groups) && groups.length > 0 && ROLE_SALES_ADMIN_GROUPS.every((sag) => groups.includes(sag));
}

export function isAgencyAdminRole(groups = []) {
  return Array.isArray(groups) && groups.length > 0 && ROLE_AGENCY_ADMIN_GROUPS.every((aag) => groups.includes(aag));
}

export function isAgencyLeaderRole(groups = []) {
  return Array.isArray(groups) && groups.length > 0 && ROLE_AGENCY_LEADERS_GROUPS.every((alr) => groups.includes(alr));
}

export function isStaffRole(groups = []) {
  return Array.isArray(groups) && groups.length > 0 && STAFF_GROUPS.every((sg) => groups.includes(sg));
}

// ── Vietnam access level ───────────────────────────────────────────────────
// L3 — Agent / Staff (external, ground-level)
export function isAccessLevelL3(role) {
  return role === ROLE_AGENT || role === ROLE_STAFF;
}

// L2 — External roles that are not L3 (e.g. Agency Leader / Agency Admin)
export function isAccessLevelL2(role) {
  return role !== ROLE_HEADQUARTERS && !isAccessLevelL3(role);
}

// L1 — Headquarters / internal
export function isAccessLevelL1(role) {
  return role === ROLE_HEADQUARTERS;
}

export function getUserRole(groups, isExternalCompanyBranch) {
  if (groups) {
    switch (true) {
      case isHeadquartersRole(groups):
        return ROLE_HEADQUARTERS;
      case isSupervisorRole(groups):
        return ROLE_SUPERVISOR;
      case isSalesManagerRole(groups):
        return ROLE_SALES_MANAGER;
      case isSalesAdminRole(groups):
        return ROLE_SALES_ADMIN;
      case isAgencyAdminRole(groups) && isExternalCompanyBranch:
        return ROLE_AGENCY_ADMIN;
      case isAgencyLeaderRole(groups) && isExternalCompanyBranch:
        return ROLE_AGENCY_LEADER;
      case isStaffRole(groups) && isExternalCompanyBranch:
        return ROLE_AGENT;
      default:
        return ROLE_STAFF;
    }
  }
  return ROLE_STAFF;
}

// CPI-1197: Check if user should have L1-level access to see all customers/opportunities.
// Controlled by country feature flag 'level2ViewAll'.
// Grants access to Sales Admin role only.
// Renamed from hasVietnamL2ViewAll to be more generic and extensible for other countries.
export function hasL2ViewAllAccess(role, features) {
  const hasFeature = features?.level2ViewAll;
  const isSalesAdmin = role === ROLE_SALES_ADMIN;
  return hasFeature && isSalesAdmin;
}

// Deprecated: Use hasL2ViewAllAccess instead
// Kept for backward compatibility
export function hasVietnamL2ViewAll(role, features) {
  return hasL2ViewAllAccess(role, features);
}

// CPI-1213: Who may re-assign the agent on a booking from the CRM booking detail page.
// Vietnam only — gated by the country feature flag 'reassignAgent'. The booking detail
// page itself is already VN-exclusive (reached only when features.TokenPayment is set),
// so this flag is an explicit kill-switch rather than the sole guard.
// Note: deliberately NOT gated on hasHigherAccess — UPPER_GROUPS excludes
// DEVELOPER_AGENT_LEADER and DEVELOPER_AGENCY_ADMIN, which are two of the four
// roles this feature is for.
export const REASSIGN_AGENT_ROLES = [
  ROLE_HEADQUARTERS,
  ROLE_SALES_ADMIN,
  ROLE_AGENCY_LEADER,
  ROLE_AGENCY_ADMIN,
];

export function canReassignAgent(role, features) {
  return Boolean(features?.reassignAgent) && REASSIGN_AGENT_ROLES.includes(role);
}

// CPI-848: who may correct a purchaser's SNAPSHOT on a VN booking - the copy taken
// at payment verification. The edit goes to the snapshot only - never to the customer's
// live contact - and the server limits it to non-identity fields (no name, mobile, email or
// ID number). Headquarters approves bookings and Sales Admin has L1-level VN access
// (CPI-1197); agents and agency roles stay read-only. Same shape as REASSIGN_AGENT_ROLES.
export const EDIT_SNAPSHOT_ROLES = [
  ROLE_HEADQUARTERS,
  ROLE_SALES_ADMIN,
];

export function canEditSnapshot(role, features) {
  return Boolean(features?.bookingConcept) && EDIT_SNAPSHOT_ROLES.includes(role);
}

// CPI-1213: the branch the agent picker is scoped to, and the guard on the control itself —
// an empty result means the caller must not render it, because SearchableAssignee lists every
// branch when handed an empty `restrictBranchOptions`.
//
// Agency Leader / Agency Admin never reach outside their own agency, whatever the booking says.
// Every other permitted role follows the booking: the agent who currently owns it, falling back
// to the booking's own company_branch_id so an unassigned booking still has a scope. The API
// keeps the two in step — createOpportunity and assignOpportunity both stamp the opportunity's
// branch from the assigned agent — so the fallback only decides the unassigned case. Note that
// on a booking created with no agent the stored branch is the *creator's*, so a booking entered
// by developer-side Sales Admin scopes to that internal branch, not to an agency.
export function getReassignBranchScope(user, opportunity, assignedUser) {
  if ([ROLE_AGENCY_LEADER, ROLE_AGENCY_ADMIN].includes(user?.role)) {
    return user?.companyBranch?.id || '';
  }
  return assignedUser?.company_branch_id || opportunity?.company_branch_id || '';
}
